import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import MailerLiteReport from './MailerLiteReport';

export default function MailerLiteTasks() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [disconnected, setDisconnected] = useState(false);

  useEffect(() => {
    setJobs([]);
    try {
      const stored = JSON.parse(localStorage.getItem(`mailerlite-dismissed-${user?.id}`) || '[]');
      setDismissed(Array.isArray(stored) ? stored : []);
    } catch { setDismissed([]); }
    if (!user?.id) return;
    let cancelled = false;
    let timer;
    let request = 0;
    const refresh = async () => {
      clearTimeout(timer);
      const current = ++request;
      try {
        const { data } = await axios.get(`${API}/mailerlite/jobs`);
        if (cancelled || current !== request) return;
        setJobs(Array.isArray(data) ? data : []);
        setDisconnected(false);
      } catch {
        if (!cancelled && current === request) setDisconnected(true);
      } finally {
        if (!cancelled && current === request) timer = setTimeout(refresh, 3000);
      }
    };
    refresh();
    window.addEventListener('mailerlite-job-started', refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('mailerlite-job-started', refresh);
    };
  }, [user?.id]);

  const dismiss = id => {
    const ids = [...dismissed, id].slice(-100);
    setDismissed(ids);
    try { localStorage.setItem(`mailerlite-dismissed-${user.id}`, JSON.stringify(ids)); } catch { /* Storage may be unavailable. */ }
  };

  const visible = jobs.filter(job => !dismissed.includes(job.id));
  if (!user || !visible.length) return null;
  return (
    <aside aria-label="MailerLite-Exporte" className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] max-h-[50vh] overflow-y-auto space-y-2">
      {visible.map(job => {
        const finished = ['completed', 'failed'].includes(job.status);
        const percentage = job.status === 'completed' ? 100 : job.total ? Math.round(job.completed / job.total * 100) : 0;
        return (
          <div key={job.id} className="rounded-lg border bg-background p-4 shadow-lg space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">MailerLite-Export</p>
              {finished && <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Exportmeldung schließen" onClick={() => dismiss(job.id)}><X className="h-4 w-4" /></Button>}
            </div>
            <Progress value={percentage} aria-label="Exportfortschritt" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100} />
            <p role="status" className="text-xs text-muted-foreground">
              {job.status === 'failed' ? job.error : job.status === 'completed'
                ? (job.result?.failed ? 'Export mit Fehlern abgeschlossen' : 'Export abgeschlossen')
                : job.total ? `${job.completed} von ${job.total} Kontakten verarbeitet (${percentage} %)` : 'Export wird vorbereitet …'}
            </p>
            <MailerLiteReport result={job.result} />
            {disconnected && !finished && <p className="text-xs text-muted-foreground">Verbindung unterbrochen. Status wird erneut abgefragt.</p>}
          </div>
        );
      })}
    </aside>
  );
}
